import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, Layers, Filter, Package, ScanLine, Download, Globe } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_LABELS = {
    pt: {
        estrutura: "Estrutura", cobertura: "Cobertura", equipamento: "Equipamento",
        ferragens: "Ferragens", energia: "Energia", iluminacao: "Iluminação",
        painel_solar: "Painel Solar", acessorio: "Acessório",
    },
    en: {
        estrutura: "Structure", cobertura: "Canopy", equipamento: "Equipment",
        ferragens: "Hardware", energia: "Power", iluminacao: "Lighting",
        painel_solar: "Solar Panel", acessorio: "Accessory",
    },
};

const MODEL_LABELS = { alfa: "Alfa", omega: "Ômega", custom: "Personalizado" };

export default function AdminPartLabelSheet() {
    const [carts, setCarts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [filterKind, setFilterKind] = useState("cart"); // cart | category | model
    const [cartId, setCartId] = useState("");
    const [category, setCategory] = useState("");
    const [model, setModel] = useState("");
    const [lang, setLang] = useState("pt"); // pt | en
    const [parts, setParts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [barcodes, setBarcodes] = useState({}); // partId → blob URL
    const [qrs, setQrs] = useState({}); // partId → blob URL

    // Load reference data
    useEffect(() => {
        (async () => {
            try {
                const [c, cat] = await Promise.all([
                    api.get("/admin/carts"),
                    api.get("/admin/cart-part-categories"),
                ]);
                setCarts(c.data || []);
                setCategories(cat.data || []);
                if (c.data && c.data.length && !cartId) setCartId(c.data[0].id);
                if (cat.data && cat.data.length && !category) setCategory(cat.data[0].value);
            } catch (err) {
                toast.error(formatApiError(err?.response?.data?.detail) || "Erro a carregar dados.");
            }
        })();
    }, []);   

    // Cleanup blob URLs when parts change
    useEffect(() => {
        return () => {
            Object.values(barcodes).forEach((u) => u && URL.revokeObjectURL(u));
        };
         
    }, []);

    async function generate() {
        // Clear previous blob URLs
        Object.values(barcodes).forEach((u) => u && URL.revokeObjectURL(u));
        Object.values(qrs).forEach((u) => u && URL.revokeObjectURL(u));
        setBarcodes({});
        setQrs({});

        setLoading(true);
        try {
            const params = {};
            if (filterKind === "cart" && cartId) params.cart_id = cartId;
            if (filterKind === "category" && category) params.category = category;
            if (filterKind === "model" && model) params.model = model;
            const { data } = await api.get("/admin/parts", { params });
            setParts(data || []);
            // Pre-load barcodes + QRs in parallel
            const publicBase = window.location.origin;
            const results = await Promise.all(
                (data || []).map(async (p) => {
                    let bc = null, qr = null;
                    try {
                        const r = await api.get(`/admin/parts/${p.id}/barcode.png?size=small`, { responseType: "blob" });
                        bc = URL.createObjectURL(r.data);
                    } catch { /* noop */ }
                    try {
                        const r = await api.get(`/admin/parts/${p.id}/qr.png?size=3&public_base=${encodeURIComponent(publicBase)}`, { responseType: "blob" });
                        qr = URL.createObjectURL(r.data);
                    } catch { /* noop */ }
                    return [p.id, { bc, qr }];
                })
            );
            const bcMap = {}, qrMap = {};
            results.forEach(([id, obj]) => { bcMap[id] = obj.bc; qrMap[id] = obj.qr; });
            setBarcodes(bcMap);
            setQrs(qrMap);
            if (!data || data.length === 0) toast.info("Nenhuma peça corresponde ao filtro.");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro a gerar plano.");
        } finally {
            setLoading(false);
        }
    }

    const activeFilterLabel = useMemo(() => {
        if (filterKind === "cart") {
            const c = carts.find((x) => x.id === cartId);
            return c ? `Carrinho ${c.serial_number}${c.nickname ? " · " + c.nickname : ""}` : "Carrinho";
        }
        if (filterKind === "category") return `Categoria · ${CATEGORY_LABELS[lang][category] || category}`;
        if (filterKind === "model") return `Modelo · ${MODEL_LABELS[model] || model}`;
        return "";
    }, [filterKind, cartId, category, model, lang, carts]);

    async function downloadCsv() {
        try {
            const params = { lang };
            if (filterKind === "cart" && cartId) params.cart_id = cartId;
            if (filterKind === "category" && category) params.category = category;
            if (filterKind === "model" && model) params.model = model;
            const res = await api.get("/admin/parts.csv", { params, responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            const parts = ["NOVIFOOD", lang === "en" ? "parts" : "pecas"];
            if (filterKind === "cart" && cartId) parts.push(`cart-${cartId.slice(0, 8)}`);
            if (filterKind === "category" && category) parts.push(category);
            if (filterKind === "model" && model) parts.push(`model-${model}`);
            a.download = parts.join("-") + ".csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro a exportar CSV");
        }
    }

    return (
        <div data-testid="admin-label-sheet-page">
            {/* Toolbar — hidden on print */}
            <div className="print:hidden">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <h2 className="font-heading text-2xl font-extrabold text-primary flex items-center gap-2">
                            <Layers className="h-6 w-6" /> Planos de etiquetas
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            Gere folhas A4 com etiquetas (40×25mm) prontas a imprimir e recortar.
                            4 colunas × 10 linhas = <strong>40 etiquetas por página</strong>.
                        </p>
                    </div>
                    <div className="flex gap-2 items-center">
                        <div className="inline-flex items-center gap-1 rounded-full bg-muted p-1" data-testid="lang-toggle">
                            <Globe className="h-3.5 w-3.5 text-primary/60 ml-1.5" />
                            {[
                                { v: "pt", l: "PT" },
                                { v: "en", l: "EN" },
                            ].map((o) => (
                                <button key={o.v} onClick={() => setLang(o.v)}
                                    data-testid={`lang-${o.v}`}
                                    className={`rounded-full px-2.5 py-0.5 text-xs font-bold transition ${lang === o.v ? "bg-primary text-primary-foreground" : "text-primary hover:bg-white"}`}>
                                    {o.l}
                                </button>
                            ))}
                        </div>
                        <button onClick={downloadCsv}
                            data-testid="btn-download-csv"
                            className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-xs font-bold text-primary hover:bg-muted">
                            <Download className="h-3.5 w-3.5" /> CSV
                        </button>
                        <Link to="/admin/equipments"
                            className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-xs font-bold text-primary hover:bg-muted">
                            <ArrowLeft className="h-3.5 w-3.5" /> Carrinhos
                        </Link>
                        <button onClick={() => window.print()}
                            disabled={parts.length === 0}
                            data-testid="btn-print-sheet"
                            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-bold hover:-translate-y-0.5 transition disabled:opacity-50 disabled:hover:translate-y-0">
                            <Printer className="h-4 w-4" /> Imprimir plano
                        </button>
                    </div>
                </div>

                {/* Filter panel */}
                <div className="mt-6 rounded-2xl border border-border bg-white p-5" data-testid="label-filters">
                    <div className="flex items-center gap-2 text-primary">
                        <Filter className="h-4 w-4" />
                        <span className="text-xs font-bold uppercase tracking-[0.16em]">Filtrar por</span>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                        {[
                            { k: "cart", label: "Carrinho" },
                            { k: "category", label: "Categoria" },
                            { k: "model", label: "Modelo" },
                        ].map((o) => (
                            <button key={o.k}
                                onClick={() => setFilterKind(o.k)}
                                data-testid={`filter-${o.k}`}
                                className={`rounded-full px-4 py-1.5 text-xs font-bold transition ${filterKind === o.k
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted text-primary hover:bg-primary/10"
                                    }`}>
                                {o.label}
                            </button>
                        ))}
                    </div>

                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                        {filterKind === "cart" && (
                            <label className="block">
                                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Carrinho</span>
                                <select value={cartId} onChange={(e) => setCartId(e.target.value)}
                                    data-testid="sel-cart"
                                    className="mt-1 w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white">
                                    <option value="">— Seleccione —</option>
                                    {carts.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.serial_number} {c.nickname ? `— ${c.nickname}` : ""} ({(c.assigned_parts || []).length} peças)
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                        {filterKind === "category" && (
                            <label className="block">
                                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Categoria</span>
                                <select value={category} onChange={(e) => setCategory(e.target.value)}
                                    data-testid="sel-category"
                                    className="mt-1 w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white">
                                    <option value="">— Seleccione —</option>
                                    {categories.map((c) => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                        {filterKind === "model" && (
                            <label className="block">
                                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Modelo</span>
                                <select value={model} onChange={(e) => setModel(e.target.value)}
                                    data-testid="sel-model"
                                    className="mt-1 w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white">
                                    <option value="">— Seleccione —</option>
                                    <option value="alfa">Alfa</option>
                                    <option value="omega">Ômega</option>
                                    <option value="custom">Personalizado</option>
                                </select>
                            </label>
                        )}
                        <div className="flex items-end">
                            <button onClick={generate} disabled={loading}
                                data-testid="btn-generate-sheet"
                                className="inline-flex items-center gap-2 rounded-full bg-secondary text-primary px-6 py-2.5 text-sm font-bold hover:-translate-y-0.5 transition disabled:opacity-50">
                                {loading ? "A gerar…" : <>Gerar plano</>}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mt-4 text-xs text-muted-foreground">
                    <strong>{parts.length}</strong> {parts.length === 1 ? "etiqueta" : "etiquetas"} pronta{parts.length === 1 ? "" : "s"}
                    {parts.length > 0 && <> · Estimativa: {Math.ceil(parts.length / 40)} página{parts.length > 40 ? "s" : ""} A4</>}
                </div>
            </div>

            {/* ------- LABEL SHEET (also visible on screen) ------- */}
            {parts.length > 0 && (
                <div className="mt-6 print:mt-0" data-testid="label-sheet">
                    {/* Header only on screen */}
                    <div className="print:hidden mb-2 text-xs uppercase tracking-wider font-bold text-muted-foreground">
                        Pré-visualização · {activeFilterLabel}
                    </div>
                    <div className="sheet-a4 bg-white shadow-md print:shadow-none mx-auto">
                        <div className="labels-grid">
                            {parts.map((p) => (
                                <LabelTile key={p.id} part={p} barcodeUrl={barcodes[p.id]} qrUrl={qrs[p.id]} lang={lang} />
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {parts.length === 0 && (
                <div className="print:hidden mt-8 rounded-2xl border border-dashed border-border bg-white p-10 text-center">
                    <Package className="mx-auto h-10 w-10 text-muted-foreground/60" />
                    <p className="mt-3 text-sm text-muted-foreground">
                        Escolha um filtro acima e clique em <strong>Gerar plano</strong> para pré-visualizar as etiquetas.
                    </p>
                </div>
            )}

            <style>{`
                .sheet-a4 {
                    width: 210mm;
                    min-height: 297mm;
                    padding: 8mm;
                    box-sizing: border-box;
                }
                .labels-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 40mm);
                    grid-auto-rows: 25mm;
                    gap: 8mm 18mm;
                    justify-content: space-between;
                    align-content: start;
                }
                @media (max-width: 900px) {
                    .sheet-a4 { transform: scale(0.7); transform-origin: top center; }
                }
                @media print {
                    body { background: white !important; }
                    @page { size: A4; margin: 8mm; }
                    .sheet-a4 { width: auto; min-height: 0; padding: 0; box-shadow: none !important; transform: none !important; }
                    .labels-grid { gap: 3mm 4mm; }
                    /* Force page-break every 40 labels */
                    .label-tile:nth-of-type(40n+1) { break-before: auto; }
                }
            `}</style>
        </div>
    );
}

function LabelTile({ part, barcodeUrl, qrUrl, lang = "pt" }) {
    const catLabel = CATEGORY_LABELS[lang][part.category] || "";
    return (
        <div className="label-tile bg-white border border-dashed border-border rounded-sm overflow-hidden print:border-neutral-300" data-testid={`label-tile-${part.id}`}
            style={{ width: "40mm", height: "25mm", padding: "1mm 1.5mm", boxSizing: "border-box" }}>
            <div className="h-full flex flex-col leading-tight" style={{ fontSize: "6pt" }}>
                <div className="flex items-baseline justify-between gap-1">
                    <div className="font-heading font-black text-primary" style={{ fontSize: "7pt", letterSpacing: "-0.02em" }}>NOVIFOOD</div>
                    {catLabel && (
                        <span className="rounded-sm bg-primary/10 text-primary uppercase tracking-wider font-bold px-1"
                            style={{ fontSize: "4.5pt" }}>{catLabel}</span>
                    )}
                </div>
                <div className="mt-0.5 flex-1 min-h-0">
                    <div className="font-bold text-primary truncate" style={{ fontSize: "6.5pt" }}>{part.name}</div>
                    <div className="text-muted-foreground truncate">
                        {part.cart?.serial_number && <span className="font-mono">{part.cart.serial_number}</span>}
                        {part.sku && <span> · {part.sku}</span>}
                    </div>
                </div>
                <div className="flex items-end gap-1">
                    {/* QR left */}
                    <div className="shrink-0 flex flex-col items-center">
                        {qrUrl ? (
                            <img src={qrUrl} alt={`qr ${part.id}`}
                                className="object-contain"
                                style={{ width: "8mm", height: "8mm", imageRendering: "crisp-edges" }} />
                        ) : (
                            <div style={{ width: "8mm", height: "8mm" }} className="bg-muted rounded-sm" />
                        )}
                    </div>
                    {/* Barcode right */}
                    <div className="flex-1 min-w-0 flex flex-col items-center">
                        {barcodeUrl ? (
                            <img src={barcodeUrl} alt={`bc ${part.id}`}
                                className="max-w-full object-contain"
                                style={{ height: "6mm", imageRendering: "crisp-edges" }} />
                        ) : (
                            <div className="text-muted-foreground" style={{ height: "6mm" }}>
                                <ScanLine className="h-3 w-3 inline" />
                            </div>
                        )}
                        <div className="font-mono tracking-widest text-primary leading-none" style={{ fontSize: "5.5pt" }}>{part.id}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
